import AccountSymbolStatus from './AccountSymbolStatus';
import {displayApi,type MasterDisplay} from '@/lib/forex-display-api';
import {AccountSnapshotPanel,ConnectionCell} from './AccountDisplayCells';
import ManagedInstancePanel from './ManagedInstancePanel';
import { Component, type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi } from "@/lib/forex-api";
import MasterProvisioningStatus from "./MasterProvisioningStatus";
import MasterTradeHistoryTab from "./MasterTradeHistoryTab";

class TradeHistoryErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) {
      return <div role="alert" className="rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-600">交易记录显示异常。<button className="ml-2 underline" onClick={() => this.setState({ failed: false })}>重试</button></div>;
    }
    return this.props.children;
  }
}

const statusChip: Record<string, { cls: string; label: string }> = {
  active: { cls: "bg-emerald-500/10 text-emerald-500", label: "已启用" },
  suspended: { cls: "bg-red-500/10 text-red-500", label: "已暂停" },
  verifying: { cls: "bg-amber-500/10 text-amber-500", label: "验证中" },
  error: { cls: "bg-red-500/10 text-red-500", label: "异常" },
};

function formatTime(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function MasterDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("id") || "";
  const [account, setAccount] = useState<MasterDisplay | null>(null);


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "trades">("overview");

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    let pending = false;
    const refresh = () => {
      if (pending) return;
      pending = true;
      return displayApi.master(accountId).then(matched=>{if(cancelled)return;setError('');setAccount(matched);})
      .catch(() => {
        if (!cancelled) setError("主账号详情接口不可用，请稍后重试。");
      })
      .finally(() => {
        pending = false;
        if (!cancelled) setLoading(false);
      });
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [accountId]);

  const handleStatus = async (status: "active" | "suspended") => {
    if (!account) return;
    setSaving(true);
    try {
      const updated = await forexApi.patchMasterAccount(account.master_account_id, { status });
      setAccount(await displayApi.master(updated.master_account_id));
      showToast("success", status === "active" ? "主账号已启用" : "主账号已暂停");
    } catch {
      showToast("error", "主账号状态更新失败，请确认后端服务和登录态");
    } finally {
      setSaving(false);
    }
  };

  const st = account ? statusChip[account.status] || { cls: "bg-surface text-text-secondary", label: account.status } : null;

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "账号与终端", href: "/forex/master-accounts" }, { label: "主账号详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">主账号详情</h2>
          <p className="text-xs text-text-muted mt-0.5">{account ? `${account.broker} · ${account.master_account_id} · ${account.account_number_masked}` : accountId || "未选择主账号"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {account?.status === "active" && <button onClick={() => handleStatus("suspended")} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-50 cursor-pointer whitespace-nowrap"><i className="ri-pause-line text-sm" />暂停</button>}
          {account && account.status !== "active" && <button onClick={() => handleStatus("active")} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 disabled:opacity-50 cursor-pointer whitespace-nowrap"><i className="ri-play-line text-sm" />启用</button>}
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回列表</button>
        </div>
      </div>

      {account && <MasterProvisioningStatus account={account} />}
      {account && <><AccountSymbolStatus facts={account}/><AccountSnapshotPanel facts={account}/><>{account.instance&&<ManagedInstancePanel key={account.instance.job_id} jobId={account.instance.job_id}/>}</></>}

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && account && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">状态</p><p className={`text-xs font-semibold mt-1 ${st?.cls}`}>{st?.label}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">连接状态</p><p className={`text-xs font-semibold mt-1 ${account.connection.state === "online" ? "text-emerald-500" : "text-amber-500"}`}><ConnectionCell facts={account}/></p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">Terminal 数</p><p className="text-lg font-semibold font-mono text-text-primary">{account.instance ? 1 : account.identity_status==='conflict'?'—':0}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">更新时间</p><p className="text-xs font-mono text-text-primary mt-1">{formatTime(account.updated_at)}</p></div>
          </div>

          <div className="flex gap-1 border-b border-surface-border">
            {([['overview', '账号概览'], ['trades', '交易记录']] as const).map(([key, label]) => (
              <button key={key} onClick={() => setActiveTab(key)} className={`border-b-2 px-4 py-2 text-xs font-medium transition-colors ${activeTab === key ? "border-brand text-brand" : "border-transparent text-text-muted hover:text-text-primary"}`}>{label}</button>
            ))}
          </div>

          {activeTab === "trades" && <TradeHistoryErrorBoundary><MasterTradeHistoryTab masterAccountId={account.master_account_id} /></TradeHistoryErrorBoundary>}

          {activeTab === "overview" && <>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">基本信息</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: "主账号ID", value: account.master_account_id, mono: true },
                  { label: "经纪商", value: account.broker },
                  { label: "服务器", value: account.server, mono: true },
                  { label: "账号", value: account.account_number_masked, mono: true },

                  { label: "状态", value: account.status },
                ].map((row) => (
                  <div key={row.label} className="bg-surface/50 rounded-md p-2.5">
                    <span className="text-[10px] text-text-muted font-medium uppercase">{row.label}</span>
                    <p className={`text-xs text-text-primary mt-0.5 ${row.mono ? "font-mono" : ""}`}>{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-text-primary mb-2">支持品种</h4>
                <div className="flex flex-wrap gap-1.5">
                  {account.supported_symbols.map((symbol) => <span key={symbol} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-500">{symbol}</span>)}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">关联 Terminal</h3>
              <div className="space-y-2">
                {account.instance?<Link className="text-brand text-sm" to={`/forex/terminal/detail?id=${encodeURIComponent(account.instance.terminal_id)}&job_id=${encodeURIComponent(account.instance.job_id)}`}>查看当前实例</Link>:<p className="text-sm text-text-muted">暂无当前实例</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-3">引用智能体</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {account.agents.map((agent) => (
                <Link key={agent.agent_id} to={`/forex/agents/detail?id=${agent.agent_id}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-surface hover:bg-brand/10 cursor-pointer">
                  <span className="text-xs text-text-primary">{agent.name}</span>
                  <span className="text-xs text-text-muted font-mono">{agent.agent_id}</span>
                </Link>
              ))}
              {account.agents.length === 0 && <p className="text-xs text-text-muted">暂无智能体直接引用该主账号</p>}
            </div>
          </div>
          </>}
        </>
      )}

      {toast && (<div className="fixed bottom-6 right-6 z-50"><div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}><i className={`${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} text-base`} />{toast.msg}</div></div>)}
    </div>
  );
}
